import React from "react";
import { styled, Theme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import HighlightIcon from "@mui/icons-material/Highlight";
import HighlightOutlinedIcon from "@mui/icons-material/HighlightOutlined";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";
import { useChangeTheme } from "../Contexts/ThemeContext";
import { useAuth } from "../Contexts/AuthContext";

const drawerWidth = 240;

// Styled AppBar with gradient background and accent border
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "sidebarOpen",
})<{ sidebarOpen?: boolean }>(({ theme, sidebarOpen }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
      : `linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)`,
  borderBottom: `2px solid ${theme.palette.accent?.main ?? "#F59E0B"}`,
  boxShadow:
    theme.palette.mode === "light"
      ? "0 2px 8px rgba(0, 0, 0, 0.15)"
      : "0 2px 8px rgba(0, 0, 0, 0.3)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(sidebarOpen && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Logo button container with hover animations
const LogoButton = styled("div", {
  shouldForwardProp: (prop) => prop !== "hidden",
})<{ hidden?: boolean }>(({ theme, hidden }) => ({
  marginRight: 10,
  display: hidden ? "none" : "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "55px",
  width: "55px",
  cursor: "pointer",
  borderRadius: 8,
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    transform: "scale(1.05)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
  },
}));

// Theme toggle button with rotation animation
const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor:
    theme.palette.mode === "light"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(255, 255, 255, 0.05)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(255, 255, 255, 0.1)",
    "& .MuiSvgIcon-root": {
      transform: "rotate(15deg)",
    },
  },
  "& .MuiSvgIcon-root": {
    color:
      theme.palette.mode === "light"
        ? theme.palette.accent?.dark ?? "#D97706"
        : theme.palette.accent?.main ?? "#FBBF24",
    transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
}));

// User avatar with accent colors and border
const UserAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.accent?.main ?? "#F59E0B",
  color: theme.palette.accent?.contrastText ?? "#000",
  border: "2px solid rgba(255, 255, 255, 0.8)",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
}));

// User button with hover glow effect
const UserButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: 4,
  minWidth: "auto",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "& .MuiAvatar-root": {
      boxShadow: `0 0 12px ${theme.palette.accent?.main ?? "#F59E0B"}80`,
    },
  },
}));

// Styled Menu with enhanced paper styling
const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    minWidth: 150,
    marginTop: 8,
    boxShadow:
      theme.palette.mode === "light"
        ? "0 4px 20px rgba(0, 0, 0, 0.1)"
        : "0 4px 20px rgba(0, 0, 0, 0.3)",
    border: `1px solid ${theme.palette.surface?.border ?? theme.palette.divider}`,
  },
  "& .MuiMenuItem-root": {
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "10px 16px",
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: `${theme.palette.accent?.main ?? "#F59E0B"}15`,
    },
  },
}));

// Spacer component
const Spacer = styled("div")({
  flex: "1 1 auto",
});

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const [userButtonAnchorEl, setUserButtonAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const handleClickUserButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setUserButtonAnchorEl(event.currentTarget);
  };

  const handleDrawerOpen = () => {
    setSidebarOpen(true);
  };

  const theme = useTheme();
  const changeTheme = useChangeTheme();

  const handleTogglePaletteType = () => {
    const paletteType = theme.palette.mode === "light" ? "dark" : "light";
    changeTheme({ paletteType });
  };

  const Auth: any = useAuth();

  return (
    <StyledAppBar position="fixed" sidebarOpen={sidebarOpen}>
      <Toolbar>
        <LogoButton onClick={handleDrawerOpen} hidden={sidebarOpen}>
          <Box
            component="img"
            src="/assets/logo.png"
            alt="pms-logo"
            sx={{ width: 50, height: "auto" }}
          />
        </LogoButton>
        <div id="AppInfoBar" />
        <Spacer />
        <Box id="AppCenterInfoBar" sx={{ display: "flex" }} />
        <Spacer />
        <Box id="AppCmdBar" sx={{ display: "flex", alignItems: "center" }} />
        <Tooltip title="Toggle Theme" enterDelay={300}>
          <ThemeToggleButton
            color="inherit"
            onClick={handleTogglePaletteType}
            aria-label="toggleTheme"
            data-ga-event-category="AppBar"
            data-ga-event-action="dark"
          >
            {theme.palette.mode === "light" ? (
              <HighlightOutlinedIcon />
            ) : (
              <HighlightIcon />
            )}
          </ThemeToggleButton>
        </Tooltip>
        {Auth.LoggedInUserID < 0 ? (
          <Button color="inherit" onClick={() => Auth.showLogin()}>
            Login
          </Button>
        ) : (
          <>
            <UserButton color="inherit" onClick={handleClickUserButton}>
              <UserAvatar>
                {(() => {
                  const name = Auth.LoggedInUserName ?? "";
                  const initials = name.match(/\b\w/g) || [];
                  return (
                    (initials.shift() || "") + (initials.pop() || "")
                  ).toUpperCase();
                })()}
              </UserAvatar>
            </UserButton>
            <StyledMenu
              id="user-menu"
              anchorEl={userButtonAnchorEl}
              keepMounted
              open={Boolean(userButtonAnchorEl)}
              onClose={() => setUserButtonAnchorEl(null)}
            >
              <MenuItem
                onClick={() => setUserButtonAnchorEl(null)}
                component={ProfileLink}
              >
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  Auth.logout();
                  setUserButtonAnchorEl(null);
                }}
              >
                Logout
              </MenuItem>
            </StyledMenu>
          </>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;

function ProfileLink(props: any) {
  return <RouterLink to="/profile" {...props} />;
}
